/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the edit quarantined stock modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, MenuItem } from '@material-ui/core';

import { FormRenderProps, Field } from 'react-final-form';
import { useSnackbar } from 'notistack';

import * as Yup from 'yup';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { AsyncForm } from '../../../components/FinalForm/AsyncForm';

import HiddenField from '../../../components/FinalForm/FinalFormControls/HiddenField';
import { PharmacyActions, IActionQuarantinedStockForm } from '../../../store/modules/drugmanagement/pharmacy';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import * as Dtos from '../../../api/Neon.Dtos';
import useAsyncFunction from '../../../hooks/useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IEditQuarantinedStockModalProps {
    pharmacyId: number;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IEditQuarantinedStockFormProps extends FormRenderProps {
    pharmacyId: number;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IActionQuarantinedStockForm>({
    id: Yup.number().required(),
    quarantinedStockAction: Yup.number().required('Please select an action')
});

const EditQuarantinedStockForm: React.FunctionComponent<IEditQuarantinedStockFormProps> = ({
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                enqueueSnackbar(`Quarantined stock has been edited`, { variant: 'success' });

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to change the drug shipment's status: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    return (
        <>
            <DialogContent>
                Select the action to take on the quarantined stock.
                <Field
                    name="quarantinedStockAction"
                    component={SelectWrapper}
                    disabled={formDisabled}
                    label="Action"
                    formControlProps={{
                        fullWidth: true
                    }}
                >
                    <MenuItem value={Dtos.QuarantinedStockAction.ReturnStockToSiteSupply}>Return Stock to Site Supply</MenuItem>
                    <MenuItem value={Dtos.QuarantinedStockAction.DestroyStock}>Destroy Stock</MenuItem>
                </Field>
                <HiddenField
                    fieldName="id"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const EditQuarantinedStockModal: React.FunctionComponent<IEditQuarantinedStockModalProps> = ({
    pharmacyId,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {
    const initialValues = Object.assign({}, { id: pharmacyId });

    const quarantineStockForm = React.useCallback((props: FormRenderProps) => {
        return (
            <EditQuarantinedStockForm
                pharmacyId={pharmacyId}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [setModalOpen, onFormSave, pharmacyId]);

    const asyncSave = useAsyncFunction(PharmacyActions.actionQuarantinedStock, PharmacyActions.actionQuarantinedStockSuccess, PharmacyActions.actionQuarantinedStockFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Edit Stock in Quarantine
                </DialogTitle>
                <AsyncForm
                    initialValues={initialValues}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={quarantineStockForm}
                />
            </Dialog>
        </>
    );
}

export default EditQuarantinedStockModal;